import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import Table from "../components/Table";
import Button from "../components/Button";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useAuthContext } from "../providers/AuthProvider";
import { useApplicationContext } from "../providers/ApplicationProvider";
import { Outlet, useNavigate } from "react-router-dom";
import { z } from "zod";
import Input from "../components/Input";
import { AccessUserType } from "../helpers/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type ContextType = {
    data: AccessUserType[];
    loading: boolean;
    refetch: () => void;
}

const Context = createContext<ContextType | null>(null);

const useAccessContext = () => {
    const context = useContext(Context);
    if (context === null) {
        throw new Error('useAccessContext must be used within the Provider');
    }
    return context;
}

const Provder = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const { apiCall, user } = useAuthContext();
    const { application } = useApplicationContext();
    const [data, setData] = useState<AccessUserType[]>([]);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const getData = async () => {
        setLoading(true);
        try {
            const response = await apiCall({
                action: 'getApplicationAccessList',
                id: application?.id
            });

            if (response.data.status === 200) {
                setData([
                    {
                        id: user?.id,
                        items: [user?.fullname, t('access_owner'), t('access_active')]
                    },
                    ...response.data.data.map((item: AccessUserType) => ({
                        id: item.id,
                        items: item.items
                    }))
                ]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // Check if user has access
        if (application?.createdby !== user?.id) {
            navigate(`/soknad/${application?.id}`)
        }

        getData();
    }, []);

    return (
        <Context.Provider value={{
            data,
            loading,
            refetch: () => {
                getData();
            }
        }}>
            {children}
        </Context.Provider>
    )
}


export const AccessContainer = () => (
    <Provder>
        <Outlet />
    </Provder>
)

const ApplicationAccess = () => {
    const { t } = useTranslation();
    const { data, refetch, loading } = useAccessContext();
    const { apiCall } = useAuthContext();
    const { application } = useApplicationContext();
    const [deleting, setDeleting] = useState(false);

    const navigate = useNavigate();

    const handleDelete = async (id: number) => {
        setDeleting(true);
        try {
            const response = await apiCall({
                action: 'removeApplicationAccessItem',
                data: {
                    application: application?.id,
                    user: id
                }
            });

            if (response.data.status === 200) {
                refetch();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setDeleting(false);
        }
    }

    const renderActions = (id: number) => (
        <>
            <FontAwesomeIcon
                icon={["fal", "ellipsis-v"]}
                data-bs-toggle="dropdown"
                aria-expanded="false"
                className="cursor-pointer px-2"
            />

            <ul className="dropdown-menu">
                <li>
                    <a className="dropdown-item" href="#" onClick={() => handleDelete(id)}>
                        {t('delete')}
                    </a>
                </li>
            </ul>
        </>
    );

    return (
        <div className="row gx-5 gy-5">
            <div className="col-lg-6">
                <h4>{t('access_title')}</h4>

                <Table
                    headings={[
                        { title: t('name') },
                        { title: t('role') },
                        { title: t('status') }
                    ]}
                    data={data}
                    settings={{
                        hover: true,
                        showSettings: true
                    }}
                    renderActions={renderActions}
                    loading={loading || deleting}
                />

                <Button
                    title={t('access_give_access')}
                    rightIcon="plus"
                    onClick={() => navigate('ny')}
                    classes="mt-3"
                />
            </div>

            <div className="col-lg-6">
                <h4>{t('access_explanation_title')}</h4>
                <p>{parse(t('access_explanation_description'))}</p>
            </div>
        </div>
    )
}

export const NewAccess = () => {
    const { t } = useTranslation();
    const { apiCall } = useAuthContext();
    const { application } = useApplicationContext();
    const { refetch } = useAccessContext();
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [apiResponse, setApiResponse] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const EmailSchema = z.string().email();

    const navigate = useNavigate();

    const handleChange = (value: string) => {
        setEmail(value);
        if (apiResponse) setApiResponse(null);

        const result = EmailSchema.safeParse(value);
        if (!result.success) {
            setError(t('input_email_error'))
        } else {
            setError('');
        }
    }

    const handleInvite = async () => {
        setLoading(true);
        try {
            const response = await apiCall({
                action: 'addApplicationAccessItem',
                data: {
                    application: application?.id,
                    email: email
                }
            });

            if (response.data.status === 200) {
                refetch();
                navigate(-1);
            } else {
                setApiResponse(response.data.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="row">
            <h4>Legg til bidragsyter</h4>

            <div className="col-lg-4 mt-4">
                <Input
                    label="e-post"
                    name="access-email"
                    type="text"
                    placeholder="e-postadresse"
                    required
                    rules={{}}
                    autoComplete="email"
                    value={email}
                    onChange={handleChange}
                    error={error}
                />

                {apiResponse ? (
                    <p className="text-danger mt-1 small">{apiResponse}</p>
                ) : null}

                <div className="d-flex justify-content-between mt-3">
                    <Button
                        title={t('back')}
                        type="outline-primary"
                        leftIcon="arrow-left"
                        onClick={() => navigate(-1)}
                    />

                    <Button
                        title="Inviter"
                        onClick={handleInvite}
                        rightIcon="paper-plane"
                        loading={loading}
                        disabled={!email || (error && error.length > 0 ? true : false)}
                    />
                </div>


            </div>
        </div>
    )
}

export default ApplicationAccess;